import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, ProtectedRoute } from './components';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import PawLoader from './components/PawLoader';
import MAINTENANCE_CONFIG from './config/maintenance';

// Route-level code splitting: each page loads as a separate JS chunk.
// Only the chunk for the current route is downloaded — not the entire app.
const Login = React.lazy(() => import('./pages/Login'));
const UnifiedLogin = React.lazy(() => import('./pages/UnifiedLogin'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ClinicOverview = React.lazy(() => import('./pages/ClinicOverview'));
const Search = React.lazy(() => import('./pages/Search'));
const ClinicalIntelligence = React.lazy(() => import('./pages/ClinicalIntelligence'));
const VaccinatorActivity = React.lazy(() => import('./pages/VaccinatorActivity'));
const ComprehensiveAnalytics = React.lazy(() => import('./pages/ComprehensiveAnalytics'));
const GeographicAnalytics = React.lazy(() => import('./pages/GeographicAnalytics'));
const ReminderBreakdown = React.lazy(() => import('./pages/ReminderBreakdown'));
const MaintenanceScreen = React.lazy(() => import('./pages/maintenance/MaintenanceScreen'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Maintenance Mode Wrapper Component
const MaintenanceWrapper = ({ children }) => {
  if (MAINTENANCE_CONFIG.MAINTENANCE_MODE) {
    return <MaintenanceScreen />;
  }
  return children;
};

// Private clinics get their own Clinic Overview page; the government analytics
// dashboard stays at /dashboard. Branching here rather than inside the pages keeps
// the redirect from firing their data fetches before they unmount.
//
// The two guards below branch on the same isPrivateOrg predicate and send its two
// cases to opposite routes, so no combination can loop. An org with a missing or
// unrecognised type is not private, and therefore lands on /dashboard from either
// direction rather than falling between the two.
const useIsPrivateOrg = () => {
  const { getOrganizationType } = useAuth();
  const organizationType = getOrganizationType();
  return Boolean(organizationType && organizationType.toLowerCase() === 'private');
};

const DashboardByOrgType = () => {
  const isPrivateOrg = useIsPrivateOrg();

  if (isPrivateOrg) return <Navigate to="/clinic-overview" replace />;
  return <Dashboard />;
};

// Mirror of the above. Without it, anything that leaves a government user sitting on
// /clinic-overview — switching clinics reloads in place, or a typed URL — renders the
// private product for a government org.
const ClinicOverviewByOrgType = () => {
  const isPrivateOrg = useIsPrivateOrg();

  if (!isPrivateOrg) return <Navigate to="/dashboard" replace />;
  return <ClinicOverview />;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <Router>
        <MaintenanceWrapper>
          <Suspense fallback={<PawLoader fullPage size="lg" />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unified-login" element={<UnifiedLogin />} />
            <Route path="/maintenance" element={<MaintenanceScreen />} />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Navigate to="/dashboard" replace />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout>
                <DashboardByOrgType />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/clinic-overview" element={
            <ProtectedRoute>
              <Layout>
                <ClinicOverviewByOrgType />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/search" element={
            <ProtectedRoute>
              <Layout>
                <Search />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/clinical-intelligence" element={
            <ProtectedRoute>
              <Layout>
                <ClinicalIntelligence />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/vaccinator-activity" element={
            <ProtectedRoute>
              <Layout>
                <VaccinatorActivity />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/comprehensive-analytics" element={
            <ProtectedRoute>
              <Layout>
                <ComprehensiveAnalytics />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/geographic-analytics" element={
            <ProtectedRoute>
              <Layout>
                <GeographicAnalytics />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/reminder-breakdown" element={
            <ProtectedRoute>
              <Layout>
                <ReminderBreakdown />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </MaintenanceWrapper>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;















